import React, { forwardRef } from 'react';
import TextField from '@mui/material/TextField';
import { useFormContext } from 'react-hook-form';
import { TimePicker as MUITimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';
import ReadOnly from './ReadOnly';

/**
 * Converts a "HH:mm" 24-hour time string to "hh:mm A" 12-hour format using dayjs.
 * @param {string} time24 - Time string in "HH:mm" format (e.g., "13:45").
 * @returns {string} Time in "hh:mm A" format (e.g., "01:45 PM").
 */
export function formatToAmPm(time24) {
  try {
    return dayjs(time24, 'HH:mm').format('hh:mma');
  } catch {
    return time24;
  }
}

const TimePicker = forwardRef(
  (
    { overwriteProps = {}, onChange, value, error, helperText, ...props },
    ref,
  ) => {
    const { getValues } = useFormContext();
    const { isHidden, isReadOnly } = useEntityPermission();

    const currentvalue = getValues(props?.name);

    if (isReadOnly(props?.permission, currentvalue)) {
      return (
        <ReadOnly name={props?.name}>
          {currentvalue ? formatToAmPm(currentvalue) : ''}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={props?.name} value={currentvalue} />;
    }

    return (
      <MUITimePicker
        dense
        fullWidth
        value={value ? dayjs(value, 'HH:mm') : null}
        {...props}
        onChange={e => {
          const target = {
            name: props?.name,
            value: dayjs(e).format('HH:mm') || null,
          };
          onChange({ target });
        }}
        slotProps={{
          ...(props?.slotProps || {}),
          textField: {
            ...(props?.slotProps?.textField || {}),
            size: props?.slotProps?.textField?.size || 'small',
          },
        }}
        sx={{
          '& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer':
            {
              padding: '6px 0px 6px 6px!important',
            },
          minWidth: '100%',
          ...(props?.sx || {}),
        }}
        renderInput={params => (
          <TextField
            {...params}
            ref={ref}
            error={!!error}
            helperText={error || helperText}
            variant="outlined"
            {...props}
            fullWidth
            size="small"
            autoComplete="off"
            {...overwriteProps}
          />
        )}
      />
    );
  },
);

export default TimePicker;
